import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { HStack, LookoButton, VStack } from '@looko-design-system';
import { Typography } from '@mui/material';

import { analyticsService } from '@/app/analytics';
import { useLastMatches } from '@/hook/useLastMatches';
import { useScreenType } from '@/hook/useMobileView';
import { useAppStoreHandler } from '@/hook/useNavigateStore';

/**
 * 전역 레이아웃 컴포넌트
 *
 * 애플리케이션의 최상위 레이아웃을 담당하는 컴포넌트입니다.
 * - HTML 메타데이터 설정 (Helmet)
 * - 모바일 환경에서 앱 설치 유도 배너
 * - 화면 타입별 반응형 처리
 * - 전역 스타일 및 구조 제공
 *
 * @description 모든 페이지의 공통 레이아웃
 */
export const GlobalLayout = React.memo(function GlobalLayout({ children }: { children: React.ReactNode }) {
  const screenType = useScreenType();

  const handleNavigateHandler = useAppStoreHandler();

  return (
    <VStack width={'100%'} height={'100%'}>
      <DefaultHelmet />

      {screenType === 'mobile' && window.ReactNativeWebView == null && (
        <HStack
          zIndex={300}
          alignItems={'center'}
          p={2}
          justifyContent={'space-between'}
          sx={{
            background: 'linear-gradient(90deg, #EEF8FF 50%, #FBF2FF 100%)',
          }}
        >
          <Typography variant="Title_Looko">Looko AI 앱에서 더 편리하게</Typography>
          <LookoButton
            size="medium"
            sx={{ borderRadius: 6 }}
            onClick={() => {
              analyticsService.log('Any_앱으로 이동 tap');
              return handleNavigateHandler();
            }}
          >
            앱으로 이동
          </LookoButton>
        </HStack>
      )}
      {children}
    </VStack>
  );
});

const DefaultHelmet = React.memo(function DefaultHelmet() {
  const match = useLastMatches();
  const location = useLocation();

  analyticsService.setScreenAnalytics(match?.handle?.analytics);

  React.useEffect(() => {
    analyticsService.logScreenView(match?.handle?.title);
  }, [location, match?.handle?.title]);

  return <Helmet>{match?.handle?.title != null && <title>{match.handle.title} | Looko-AI</title>}</Helmet>;
});
